import { useEffect, useRef, useState, useCallback } from "react";
import { Send, Users } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const senderLabel = (m, mine) => (mine ? "Tu" : m.from_role === "admin" ? "Super Admin" : m.from_name);

const MessageBubble = ({ m, mine }) => (
  <div className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid="chat-message">
    <div className={`max-w-[78%] px-4 py-3 ${mine ? "bg-[#0F3D3E] text-white" : "bg-white border border-zinc-200"}`}>
      {m.class_name && (
        <p className={`flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest mb-1 ${mine ? "text-[#5EEAD4]" : "text-[#0D9488]"}`}>
          <Users className="w-3 h-3" /> Classe {m.class_name}
        </p>
      )}
      <p className="text-sm leading-relaxed whitespace-pre-wrap">{m.body}</p>
      <p className={`font-mono text-[10px] mt-2 ${mine ? "text-zinc-400" : "text-zinc-500"}`}>{senderLabel(m, mine)} · {fmtDate(m.created_at, true)}</p>
    </div>
  </div>
);

const useMessages = (fetchUrl) => {
  const [msgs, setMsgs] = useState([]);
  const load = useCallback(() => api.get(fetchUrl).then((r) => setMsgs(r.data)).catch(() => {}), [fetchUrl]);
  useEffect(() => {
    setMsgs([]);
    load();
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [load]);
  return [msgs, load];
};

const Composer = ({ onSend }) => {
  const [body, setBody] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    if (body.trim() && (await onSend(body))) setBody("");
  };
  return (
    <form onSubmit={submit} className="flex border-t border-zinc-200">
      <input data-testid="chat-message-input" value={body} onChange={(e) => setBody(e.target.value)} placeholder="Scrivi un messaggio…" className="flex-1 h-14 px-5 text-sm focus:outline-none" />
      <button data-testid="chat-send-btn" className="w-14 h-14 grid place-items-center bg-[#0D9488] text-white hover:bg-[#059669] transition-colors duration-150"><Send className="w-4 h-4" /></button>
    </form>
  );
};

export const ChatThread = ({ fetchUrl, toUserId, classTarget, title, subtitle, emptyText = "Nessun messaggio" }) => {
  const { user } = useAuth();
  const [msgs, load] = useMessages(fetchUrl);
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [msgs.length]);

  const send = async (body) => {
    try {
      await api.post("/messages", { body, to_user_id: toUserId, class_name: classTarget });
      load();
      return true;
    } catch (err) {
      toast.error(errMsg(err));
      return false;
    }
  };

  return (
    <div className="flex flex-col h-[70vh] bg-white border border-zinc-200" data-testid="chat-communication-panel">
      <div className="px-5 py-4 border-b border-zinc-200">
        <p className="font-display text-lg font-bold tracking-tight" data-testid="chat-thread-title">{title}</p>
        {subtitle && <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{subtitle}</p>}
      </div>
      <div className="flex-1 overflow-y-auto p-5 space-y-4 bg-zinc-50" data-testid="chat-messages-list">
        {msgs.length === 0 && <p className="text-sm text-zinc-400 text-center pt-10">{emptyText}</p>}
        {msgs.map((m) => <MessageBubble key={m.id} m={m} mine={m.from_id === user.id} />)}
        <div ref={endRef} />
      </div>
      <Composer onSend={send} />
    </div>
  );
};
